// Stage detail (Iris's screen 2): which lead is on which step, whose step it is, and how long it has sat there. The
// stage tabs across the top with counts (Parked and Lost dashed); the stage's steps as rows with the leads on each as
// chips. A step that applies to no lead here is dimmed.

import { Link, useNavigate, useParams } from 'react-router-dom';
import { stepAppliesToLead } from '@zerogap/engine';
import { useCrumb } from '../../frame/Frame';
import { money, monthDay } from '../../lib/format';
import { conditionWords, doneWords, usePipeline, type Lead, type Pipeline } from '../../lib/pipeline';
import { useSession } from '../../lib/session';
import { Empty, Loading } from '../../ui/kit';
import { BringBack, DaysOnStep, LeadValue, NextTouch, ParkedRow, ReviewLine, SeatTag, Strip, UndoRow, useTaps } from './bits';
import { stageTotals } from './Board';

export function StageTabs({ p, active }: { p: Pipeline; active: string }) {
  const nav = useNavigate();
  const count = (key: string) => (key === 'won' ? p.wonAll.length : key === 'parked' ? p.parked.length : key === 'lost' ? p.lost.length : p.open.filter((l) => l.stage?.key === key).length);
  const tabs = [...p.working.map((s) => ({ key: s.key, name: s.name, side: false })), { key: 'won', name: p.wonStage?.name ?? 'Won', side: false }, { key: 'parked', name: p.parkedStage?.name ?? 'Parked', side: true }, { key: 'lost', name: p.lostStage?.name ?? 'Lost', side: true }];
  return (
    <div className="ptabs" role="tablist">
      {tabs.map((t) => (
        <button key={t.key} type="button" role="tab" aria-selected={t.key === active} className={`ptab${t.key === active ? ' on' : ''}${t.side ? ' side' : ''}`} onClick={() => nav(`/pipeline/stage/${t.key}`)}>
          {t.name}
          <b className="num">{count(t.key)}</b>
        </button>
      ))}
    </div>
  );
}

function LeadChip({ p, lead }: { p: Pipeline; lead: Lead }) {
  return (
    <span className="pjob">
      <Link to={`/opportunities/${lead.id}`} className="rowlink">
        <b>{lead.name}</b>
      </Link>
      <LeadValue lead={lead} />
      <DaysOnStep lead={lead} />
      {lead.readyRow && <small>ready · waiting on you</small>}
      {lead.replyBy && !lead.needsOwner && <small>reply-by {lead.replyBy === new Date().toISOString().slice(0, 10) ? 'today' : monthDay(lead.replyBy)}</small>}
      <NextTouch lead={lead} />
      <ReviewLine p={p} lead={lead} />
    </span>
  );
}

export function StagePage() {
  const { key } = useParams();
  const p = usePipeline();
  const { can } = useSession();
  const taps = useTaps();
  const stage = p.stages.find((s) => s.key === key) ?? null;
  useCrumb([{ label: 'Home', to: '/' }, { label: 'Pipeline', to: '/pipeline' }, { label: stage?.name ?? 'Stage' }]);
  if (p.loading) return <Loading what="Loading the stage" />;
  if (!stage) return <Empty>There is no stage at this address.</Empty>;

  if (stage.kind !== 'working') {
    const leads = stage.kind === 'won' ? p.wonAll : stage.kind === 'parked' ? p.parked : p.lost;
    const totals = stageTotals(leads);
    return (
      <div className="stack">
        <StageTabs p={p} active={stage.key} />
        <div className="panel">
          <div className="lb">
            <span>
              {stage.name} · {leads.length}
              {can('see_prices') ? ` · ${money(totals.total, { whole: true })}` : ''}
            </span>
            <span>
              <Link to="/pipeline/won-lost" className="plink">
                Won and lost
              </Link>
            </span>
          </div>
          {leads.length === 0 && <Empty>Nothing here.</Empty>}
          {leads.map((l) => {
            const tap = taps.tap(l.id);
            if (tap) return <UndoRow key={l.id} tap={tap} onUndo={() => void taps.undo(l.id)} />;
            if (stage.kind === 'parked') return <ParkedRow key={l.id} lead={l} onTap={(t) => taps.remember(l.id, t)} />;
            return (
              <div key={l.id} className="pln">
                <span>
                  <Link to={`/opportunities/${l.id}`} className="rowlink">
                    <b>{l.name}</b>
                  </Link>
                  <br />
                  <small>
                    {stage.kind === 'won' ? `Won ${monthDay(l.opp.outcome_at)}${l.opp.anchor_type ? ` · ${l.opp.anchor_type} ${monthDay(l.opp.anchor_date)}` : ''}` : `Lost ${monthDay(l.opp.outcome_at)}${l.opp.outcome_note ? ` · ${l.opp.outcome_note}` : ''}${l.nextTouch ? ` · re-approach ${monthDay(l.nextTouch)}` : ''}`}
                  </small>
                  {stage.kind === 'lost' && (
                    <span className="pctl">
                      <BringBack lead={l} onTap={(t) => taps.remember(l.id, t)} small />
                    </span>
                  )}
                </span>
                <LeadValue lead={l} />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const steps = p.steps.filter((s) => s.stage_id === stage.id && !s.retired_at).sort((a, b) => a.sort - b.sort);
  const here = p.open.filter((l) => l.stage?.id === stage.id);
  const unplaced = here.filter((l) => !l.step);
  const next = p.working[p.working.findIndex((s) => s.id === stage.id) + 1] ?? p.wonStage;
  return (
    <div className="stack">
      <StageTabs p={p} active={stage.key} />
      {unplaced.length > 0 && (
        <div className="panel soft">
          {unplaced.length} lead{unplaced.length > 1 ? 's' : ''} at {stage.name} {unplaced.length > 1 ? 'are' : 'is'} not on a step yet: {unplaced.map((l) => l.name).join(', ')}.{' '}
          <Link to="/pipeline/place" className="plink">
            Placement page
          </Link>
        </div>
      )}
      <div className="psteps">
        <div className="pst head">
          <span />
          <span className="lb">Step</span>
          <span className="lb">Seat · done when</span>
          <span className="lb">On it now · days on the step</span>
        </div>
        {steps.map((st, i) => {
          const on = here.filter((l) => l.step?.id === st.id);
          // dimmed when no lead at this stage could reach the step (its condition says no for every one of them)
          const applies = here.length === 0 || on.length > 0 || here.some((l) => stepAppliesToLead(st, p.conditions, l.opp) !== 'no');
          return (
            <div key={st.id} className={`pst${applies || here.length === 0 ? '' : ' na'}`}>
              <span className="psx num">{i + 1}</span>
              <span className="pstn">
                {st.name}
                <small className="pcond">
                  {conditionWords(p, st)}
                  {st.gate ? ` · Gate · moves to ${next?.name ?? 'Won'}` : ''}
                  {st.back_to_step_id ? ` · back to: ${p.stepById(st.back_to_step_id)?.name ?? ''}` : ''}
                  {!applies && here.length > 0 ? ' · no lead here' : ''}
                </small>
              </span>
              <span>
                <SeatTag p={p} step={st} /> <span className="plv">{p.seatOf(st).seat?.key === 'client' ? '' : st.level}</span>
                <small className="pcond">{doneWords(p, st)}</small>
              </span>
              <span>
                {on.map((l) => (
                  <LeadChip key={l.id} p={p} lead={l} />
                ))}
              </span>
            </div>
          );
        })}
      </div>
      <div className="plegend">
        <b>L1</b> AI drafts, you send <b>L2</b> AI does and tells you <b>L3</b> you
      </div>
    </div>
  );
}
